import { Box, Button, Stack, Table, TableBody, TableRow, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ProductIconTile, SefanoCheckbox, StatusBadge } from "@/components/Atoms";
import {
  CopyLinkRow,
  FilterButton,
  FilterChip,
  FloatingTextField,
  QuantityStepper,
  SearchField,
  SelectField,
  SefanoButton,
  SortableColumnHeader,
} from "@/components/Molecules";
import { SefanoPagination } from "@/components/Organisms";
import type { SortDirection } from "@/components/Molecules/SortableColumnHeader/SortableColumnHeader.types";
import type { StatusBadgeColor } from "@/components/Atoms/StatusBadge/StatusBadge.types";
import * as styles from "./Page.styles";

const statusColors: StatusBadgeColor[] = ["error", "warning", "info", "success", "neutral"];

export function UiShowcasePage() {
  const { t } = useTranslation("common");
  const [quantity, setQuantity] = useState(236);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filterActive, setFilterActive] = useState(false);
  const [advancedFilterActive, setAdvancedFilterActive] = useState(true);
  const [page, setPage] = useState(1);
  const [checkboxChecked, setCheckboxChecked] = useState(true);

  return (
    <Box sx={styles.page}>
      <Typography variant="h4" sx={styles.title}>
        {t("uiShowcase.title")}
      </Typography>

      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t("uiShowcase.sections.buttons")}
        </Typography>
        <Stack sx={styles.row}>
          <SefanoButton intent="primary">{t("uiShowcase.buttons.submit")}</SefanoButton>
          <SefanoButton intent="primary" fullWidth size="small">{t("uiShowcase.buttons.submit")}</SefanoButton>
          <Button variant="contained" color="primary" size="small">{t("uiShowcase.buttons.submit")}</Button>
          <SefanoButton intent="secondary">{t("uiShowcase.buttons.cancel")}</SefanoButton>
          <SefanoButton intent="add">{t("uiShowcase.buttons.add")}</SefanoButton>
          <SefanoButton intent="cancel">{t("uiShowcase.buttons.cancelWithIcon")}</SefanoButton>
          <FilterButton>{t("uiShowcase.buttons.applyFilter")}</FilterButton>
          <FilterButton withIcon>{t("uiShowcase.buttons.applyFilter")}</FilterButton>
        </Stack>
      </Box>

      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t("uiShowcase.sections.statusBadges")}
        </Typography>
        <Stack gap={2}>
          <Stack sx={styles.badgeRow}>
            {statusColors.map((color) => (
              <StatusBadge
                key={`md-${color}`}
                color={color}
                size="md"
                label={t("uiShowcase.status.awaitingPayment")}
              />
            ))}
          </Stack>
          <Stack sx={styles.badgeRow}>
            {statusColors.map((color) => (
              <StatusBadge
                key={`sm-${color}`}
                color={color}
                size="sm"
                label={t("uiShowcase.status.awaitingPayment")}
              />
            ))}
          </Stack>
        </Stack>
      </Box>

      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t("uiShowcase.sections.formFields")}
        </Typography>
        <Stack sx={styles.column}>
          <FloatingTextField label={t("uiShowcase.fields.buyerName")} />
          <FloatingTextField
            label={t("uiShowcase.fields.buyerName")}
            defaultValue={t("uiShowcase.fields.buyerNameValue")}
          />
          <FloatingTextField
            label={t("uiShowcase.fields.buyerName")}
            defaultValue={t("uiShowcase.fields.buyerNameValue")}
            fieldState="error"
            helperText={t("uiShowcase.fields.buyerNameError")}
          />
          <FloatingTextField
            label={t("uiShowcase.fields.nationalId")}
            defaultValue="412-0666-255"
            fieldState="success"
          />
          <FloatingTextField label={t("uiShowcase.fields.nationalId")} />
          <FloatingTextField
            label={t("uiShowcase.fields.address")}
            multiline
            minRows={2}
          />
          <FloatingTextField
            label={t("uiShowcase.fields.address")}
            defaultValue={t("uiShowcase.fields.addressValue")}
            fieldState="success"
            multiline
            minRows={2}
          />
          <SelectField
            label={t("uiShowcase.fields.selectProduct")}
            displayEmpty
            value=""
            options={[
              { value: "placeholder", label: t("uiShowcase.fields.selectProduct") },
              { value: "1", label: t("uiShowcase.fields.productA") },
            ]}
          />
          <SelectField
            label={t("uiShowcase.fields.selectProduct")}
            value="1"
            options={[
              { value: "1", label: t("uiShowcase.fields.productA") },
              { value: "2", label: t("uiShowcase.fields.productB") },
            ]}
          />
        </Stack>
      </Box>

      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t("uiShowcase.sections.searchAndChips")}
        </Typography>
        <Stack gap={2}>
          <SearchField placeholder={t("uiShowcase.search.placeholder")} />
          <SearchField fieldSize="compact" placeholder={t("uiShowcase.search.placeholder")} />
          <Stack sx={styles.row}>
            <FilterChip label={t("uiShowcase.chips.accessories")} size="md" />
            <FilterChip label={t("uiShowcase.chips.accessories")} size="sm" />
            <FilterChip
              label={t("uiShowcase.chips.filter")}
              active={filterActive}
              onClick={() => setFilterActive((prev) => !prev)}
            />
            <FilterChip
              label={t("uiShowcase.chips.advancedFilter")}
              advanced
              active={advancedFilterActive}
              onClick={() => setAdvancedFilterActive((prev) => !prev)}
            />
          </Stack>
        </Stack>
      </Box>

      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t("uiShowcase.sections.checkboxes")}
        </Typography>
        <Stack gap={1}>
          <SefanoCheckbox
            label={t("uiShowcase.checkboxes.terms")}
            checked={checkboxChecked}
            onChange={setCheckboxChecked}
          />
          <SefanoCheckbox
            label={t("uiShowcase.checkboxes.terms")}
            checked={false}
            indeterminate
          />
          <SefanoCheckbox
            label={t("uiShowcase.checkboxes.terms")}
            variant="circle"
            checked={false}
          />
          <SefanoCheckbox
            label={t("uiShowcase.checkboxes.terms")}
            variant="circle"
            checked
          />
        </Stack>
      </Box>

      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t("uiShowcase.sections.quickOrder")}
        </Typography>
        <Stack gap={2} maxWidth={560}>
          <Stack direction="row" alignItems="center" gap={2}>
            <ProductIconTile />
            <QuantityStepper value={quantity} onChange={setQuantity} />
          </Stack>
          <CopyLinkRow url={t("uiShowcase.quickOrder.sampleUrl")} size="lg" copyLabel={t("uiShowcase.quickOrder.copyLink")} />
          <CopyLinkRow url={t("uiShowcase.quickOrder.sampleUrl")} size="sm" copyLabel={t("uiShowcase.quickOrder.copyLink")} />
        </Stack>
      </Box>

      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t("uiShowcase.sections.tableUi")}
        </Typography>
        <Stack gap={3}>
          <Table>
            <TableBody>
              <TableRow>
                <SortableColumnHeader
                  label={t("uiShowcase.table.time")}
                  direction={sortDirection}
                  onSort={setSortDirection}
                />
              </TableRow>
            </TableBody>
          </Table>
          <SefanoPagination count={164} page={page} onChange={(_, value) => setPage(value)} />
        </Stack>
      </Box>

      <Box sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          {t("uiShowcase.sections.reference")}
        </Typography>
        <Box
          component="img"
          src="/design/Dashboard-page.png"
          alt={t("uiShowcase.reference.alt")}
          sx={styles.referenceImage}
        />
      </Box>
    </Box>
  );
}
